import {controller, target, attr} from '@github/catalyst'
import {createConsumer} from '@rails/actioncable'

@controller
class LiveChatElement extends HTMLElement {
  @target messagesContainer: HTMLElement
  @target messageInput: HTMLInputElement
  @target charCount: HTMLElement
  @target toggleButton: HTMLButtonElement
  @target chatSidebar: HTMLElement
  @target connectionStatus: HTMLElement
  @target inputArea: HTMLElement
  @target messageTemplate: HTMLTemplateElement
  @target actionsTemplate: HTMLTemplateElement
  @target systemMessageTemplate: HTMLTemplateElement
  @target bannedNotice: HTMLElement
  // The player this chat sits beside, when there is one. Only read at send
  // time, to stamp a message with where in the broadcast its author actually
  // was — see broadcastPosition().
  @target player: HTMLElement & { currentPdt?: Date }
  @attr streamId = ''
  @attr currentLogin = ''
  // Set on the guest pass page, where there's no session to authenticate the
  // cable connection with. Chat is read-only for these viewers.
  @attr passToken = ''

  private subscription: any = null
  private consumer: any = null
  private isModerator = false
  private isSiteAdmin = false
  private MAX_MESSAGES = 100
  private lastSentAt = 0
  private NAME_COLORS = [
    'text-rose-400',
    'text-emerald-400',
    'text-sky-400',
    'text-amber-400',
  ]

  connectedCallback() {
    this.subscribe()
    this.updateCharCount()
  }

  // A guest pass travels on the cable URL — the connection has no session
  // cookie to identify it. Undefined falls back to the action-cable-url meta
  // tag, which is what createConsumer() does on its own.
  //
  // Reads the attribute rather than the `passToken` accessor: Catalyst has not
  // always installed its attr accessors by the time connectedCallback runs, and
  // the accessor then answers with the class-field default instead of the
  // markup's value.
  private cableUrl(): string | undefined {
    const token = this.getAttribute('data-pass-token') || this.passToken
    if (!token) return undefined

    const meta = document.head.querySelector<HTMLMetaElement>('meta[name="action-cable-url"]')
    const base = meta?.content || '/cable'
    return `${base}${base.includes('?') ? '&' : '?'}stream_pass=${encodeURIComponent(token)}`
  }

  disconnectedCallback() {
    this.subscription?.unsubscribe()
    this.consumer?.disconnect()
    this.subscription = null
    this.consumer = null
  }

  private subscribe() {
    this.consumer = createConsumer(this.cableUrl())
    const element = this

    this.subscription = this.consumer.subscriptions.create(
      { channel: 'LiveChatChannel', stream_id: this.streamId },
      {
        connected() {
          element.connectionStatus.hidden = true
          element.connectionStatus.textContent = ''
        },
        disconnected() {
          element.showStatus('Disconnected')
        },
        rejected() {
          element.showStatus('Chat unavailable')
        },
        received(data: any) {
          if (data.type === 'status') {
            element.isModerator = !!data.moderator
            element.isSiteAdmin = !!data.site_admin
            if (data.chat_banned) element.showBanned()
          } else if (data.type === 'ban') {
            if (data.login === element.currentLogin) element.showBanned()
            element.applyBanState(data.login, true)
          } else if (data.type === 'unban') {
            if (data.login === element.currentLogin) element.showUnbanned()
            element.applyBanState(data.login, false)
          } else if (data.type === 'delete') {
            element.markDeleted(data.id)
          } else if (data.type === 'restore') {
            element.markRestored(data.id, data.message)
          } else if (data.login && !data.type) {
            element.appendMessage(data.id, data.login, data.user_id, data.staff, data.message)
          }
        },
      }
    )
  }

  private cloneTemplate(template: HTMLTemplateElement): HTMLElement {
    return (template.content.firstElementChild as HTMLElement).cloneNode(true) as HTMLElement
  }

  // The composer is absent entirely in read-only chat (guest pass), so every
  // one of these tolerates a missing target.
  private showBanned() {
    if (this.inputArea) this.inputArea.hidden = true
    if (this.bannedNotice) this.bannedNotice.hidden = false
  }

  private showUnbanned() {
    if (this.inputArea) this.inputArea.hidden = false
    if (this.bannedNotice) this.bannedNotice.hidden = true
  }

  private showStatus(text: string) {
    this.connectionStatus.textContent = text
    this.connectionStatus.hidden = false
  }

  private appendSystemMessage(text: string) {
    const row = this.cloneTemplate(this.systemMessageTemplate)
    row.textContent = text
    this.messagesContainer.appendChild(row)
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight
  }

  private appendMessage(id: string, login: string, userId: number, staff: boolean, message: string) {
    const row = this.cloneTemplate(this.messageTemplate)
    if (id) row.dataset.messageId = id
    row.dataset.login = login

    const name = row.querySelector('[data-chat-name]') as HTMLElement
    name.classList.add(this.NAME_COLORS[userId % this.NAME_COLORS.length])
    name.textContent = login

    if (!staff) row.querySelector('[data-chat-staff-badge]')?.remove()

    const body = row.querySelector('[data-chat-body]') as HTMLElement
    body.textContent = message

    if (id && (this.isModerator || this.isSiteAdmin)) {
      // Subtle row highlight while the moderation overlay is showing, only on
      // rows that actually have moderation tools.
      row.classList.add('hover:bg-white/5')
      row.appendChild(this.buildModerationActions(id, login))
    }

    this.messagesContainer.appendChild(row)

    // Prune old messages
    while (this.messagesContainer.children.length > this.MAX_MESSAGES) {
      this.messagesContainer.removeChild(this.messagesContainer.firstChild!)
    }

    // Auto-scroll
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight
  }

  // Clones the moderation overlay and strips the actions the current viewer
  // isn't allowed to take. The server re-checks authorization on every call.
  private buildModerationActions(id: string, login: string): HTMLElement {
    const actions = this.cloneTemplate(this.actionsTemplate)

    for (const button of actions.querySelectorAll('button')) {
      button.setAttribute('data-message-id', id)
    }

    if (!this.isModerator) {
      actions.querySelector('[data-chat-delete]')?.remove()
      actions.querySelector('[data-chat-restore]')?.remove()
    }

    const ban = actions.querySelector('[data-chat-ban]')
    const unban = actions.querySelector('[data-chat-unban]')
    if (this.isSiteAdmin) {
      ban?.setAttribute('data-ban-login', login)
      ban?.setAttribute('data-tooltip', `Ban ${login} from chat`)
      ban?.setAttribute('aria-label', `Ban ${login} from chat`)
      unban?.setAttribute('data-ban-login', login)
      unban?.setAttribute('data-tooltip', `Unban ${login} from chat`)
      unban?.setAttribute('aria-label', `Unban ${login} from chat`)
    } else {
      ban?.remove()
      unban?.remove()
    }

    return actions
  }

  // Flips ban/unban buttons on every message row from the given user, driven
  // by the server's ban/unban broadcasts — this doubles as the success
  // feedback for the acting admin.
  private applyBanState(login: string, banned: boolean) {
    if (!login) return
    const rows = this.messagesContainer.querySelectorAll(`[data-login="${CSS.escape(login)}"]`)
    for (const row of rows) {
      const ban = row.querySelector('[data-chat-ban]') as HTMLElement | null
      if (ban) ban.hidden = banned
      const unban = row.querySelector('[data-chat-unban]') as HTMLElement | null
      if (unban) unban.hidden = !banned
    }
  }

  banUser(event: Event) {
    const btn = event.currentTarget as HTMLElement
    const id = btn.getAttribute('data-message-id')
    const login = btn.getAttribute('data-ban-login') || 'this user'
    if (!id || !this.subscription) return
    if (!window.confirm(`Ban ${login} from chat? This blocks them from chatting in all streams.`)) return

    this.subscription.perform('ban_user', { id })
  }

  unbanUser(event: Event) {
    const id = (event.currentTarget as HTMLElement).getAttribute('data-message-id')
    if (id && this.subscription) this.subscription.perform('unban_user', { id })
  }

  deleteMessage(event: Event) {
    const id = (event.currentTarget as HTMLElement).getAttribute('data-message-id')
    if (id && this.subscription) this.subscription.perform('delete_message', { id })
  }

  restoreMessage(event: Event) {
    const id = (event.currentTarget as HTMLElement).getAttribute('data-message-id')
    if (id && this.subscription) this.subscription.perform('restore_message', { id })
  }

  // Applied on every connected client (including the acting moderator's) via
  // the server broadcast — no optimistic local removal.
  private markDeleted(id: string) {
    const row = this.findRow(id)
    if (!row) return

    const body = row.querySelector('[data-chat-body]')
    if (body) {
      body.textContent = 'Message deleted by a moderator'
      body.className = 'text-zinc-500 italic'
    }
    this.toggleRowActions(row, { deleted: true })
  }

  private markRestored(id: string, message: string) {
    const row = this.findRow(id)
    if (!row) return

    const body = row.querySelector('[data-chat-body]')
    if (body) {
      body.textContent = message
      body.className = 'text-zinc-300'
    }
    this.toggleRowActions(row, { deleted: false })
  }

  private findRow(id: string): HTMLElement | null {
    if (!id) return null
    return this.messagesContainer.querySelector(`[data-message-id="${id}"]`)
  }

  private toggleRowActions(row: HTMLElement, { deleted }: { deleted: boolean }) {
    const del = row.querySelector('[data-chat-delete]') as HTMLElement | null
    if (del) del.hidden = deleted
    const restore = row.querySelector('[data-chat-restore]') as HTMLElement | null
    if (restore) restore.hidden = !deleted
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  insertEmoji(event: Event) {
    const emoji = (event.currentTarget as HTMLElement).getAttribute('data-emoji')
    if (!emoji) return
    const input = this.messageInput
    if (!input) return
    if (input.value.length + emoji.length > 200) return
    input.value += emoji
    this.updateCharCount()
    input.focus()
  }

  // The wall-clock instant of the frame on screen right now, from the HLS
  // program date time Mux stamps into the live manifest. Live playback always
  // trails the encoder — by tens of seconds on a standard-latency stream, and
  // by more for anyone scrubbed back — so this is what says where a message
  // belongs on the recording. Null when the player can't say: the server then
  // falls back to arrival time.
  private broadcastPosition(): string | null {
    const pdt = this.player?.currentPdt
    if (!(pdt instanceof Date) || Number.isNaN(pdt.getTime())) return null

    return pdt.toISOString()
  }

  sendMessage() {
    if (!this.messageInput) return

    const message = this.messageInput.value.trim()
    if (!message || !this.subscription) return

    const now = Date.now()
    if (now - this.lastSentAt < 1000) return
    this.lastSentAt = now

    this.subscription.perform('send_message', {
      message,
      program_date_time: this.broadcastPosition(),
    })
    this.messageInput.value = ''
    this.updateCharCount()
  }

  updateCharCount() {
    if (!this.messageInput || !this.charCount) return

    const remaining = 200 - this.messageInput.value.length
    this.charCount.textContent = `${remaining}`

    if (remaining < 20) {
      this.charCount.classList.add('text-rose-400')
      this.charCount.classList.remove('text-zinc-500')
    } else {
      this.charCount.classList.remove('text-rose-400')
      this.charCount.classList.add('text-zinc-500')
    }
  }

  toggleChat() {
    const isVisible = !this.chatSidebar.hidden
    if (isVisible) {
      this.subscription?.unsubscribe()
      this.consumer?.disconnect()
      this.subscription = null
      this.consumer = null
    } else {
      this.subscribe()
      this.appendSystemMessage('Connected to chat')
    }
    this.chatSidebar.hidden = isVisible
    this.toggleButton.hidden = !isVisible
  }
}
